import TableComponent from '../../components/table/tableComponent';
import {
  YTDPerformanceTable,
  IAMDefinedBenefitTable,
} from '../../components/data/tableData';
import { setChartTitle } from '../../redux/slices/uiSlice';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import './tableContent.css';

export default function TableContent({ data }) {
  const dispatch = useDispatch();

  const tableData = getTableData(data);
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(orientation: landscape)').matches
      : false,
  );

  useEffect(() => {
    const mm = window.matchMedia('(orientation: landscape)');
    const onChange = () => setIsLandscape(mm.matches);
    onChange();
    mm.addEventListener?.('change', onChange);
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      mm.removeEventListener?.('change', onChange);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);

  useEffect(() => {
    dispatch(setChartTitle(tableData.title));
  }, [dispatch, tableData.title]);

  if (!tableData) {
    return (
      <div className="chart-content-center">
        <p>No table data available</p>
      </div>
    );
  }

  // If data is an array (multiple tables), render multiple tables
  if (Array.isArray(tableData)) {
    return (
      <div className="chart-content-center">
        {tableData.map((table, index) => renderTable(table, index))}
      </div>
    );
  }

  // Single table
  return (
    <div className="chart-content-center">{renderTable(tableData, 0, isLandscape)}</div>
  );
}

// Function to determine which table data to use
function getTableData(data) {
  // If data is passed directly, use it
  if (data && data.columns && data.rows) {
    return data;
  }

  // If data contains a table type identifier
  if (data && typeof data === 'string') {
    switch (data.toLowerCase()) {
      case 'ytd':
      case 'ytdperformance':
      case 'ytdperformancetable':
        return YTDPerformanceTable;
      case 'iam':
      case 'iamdefinedbenefit':
      case 'iamdefinedbenefittable':
        return IAMDefinedBenefitTable;
      default:
        return YTDPerformanceTable; // Default to YTD table
    }
  }

  // If data contains table selection
  if (data && data.tableType) {
    switch (data.tableType.toLowerCase()) {
      case 'ytd':
      case 'ytdperformance':
        return YTDPerformanceTable;
      case 'iam':
      case 'iamdefinedbenefit':
        return IAMDefinedBenefitTable;
      case 'both':
      case 'all':
        return [YTDPerformanceTable, IAMDefinedBenefitTable];
      default:
        return YTDPerformanceTable;
    }
  }

  // If no specific data is provided, show both tables
  if (!data) {
    return [YTDPerformanceTable, IAMDefinedBenefitTable];
  }

  return data;
}

// Function to render a single table
function renderTable(tableData, index, isLandscape) {
  // Transform columns from the table format
  const columns = tableData.columns.map(columnName => {
    const field = getFieldName(columnName);
    return {
      field: field,
      headerName: columnName,
      width: getColumnWidth(columnName),
      type: getColumnType(columnName),
      flex: shouldFlex(columnName) ? 1 : undefined,
    };
  });

  // Transform rows from the table format
  const rows = tableData.rows.map((row, rowIndex) => {
    const transformedRow = { id: rowIndex + 1 };

    // Map each column to the corresponding field
    tableData.columns.forEach(columnName => {
      const field = getFieldName(columnName);
      transformedRow[field] = getRowValue(row, columnName);
    });

    return transformedRow;
  });

  return (
    <div className="chart-content-center">
      <div
        className={`table-wrapper ${
          isLandscape ? 'is-landscape' : 'is-portrait'
        }`}
      >
      <TableComponent rows={rows} columns={columns} />
    </div>
    </div>
  );
}

// Helper function to get field name from column header
function getFieldName(columnName) {
  const fieldMap = {
    No: 'no',
    Fund: 'fund',
    'Index ID': 'indexId',
    'Allocation (%)': 'allocation',
    Inception: 'inception',
    Strategy: 'strategy',
    'Performance (%)': 'performance',
    'Standard Deviation (%)': 'standardDeviation',
    'Sharpe Ratio': 'sharpeRatio',
    'Oct-07': 'oct07',
    'Nov-07': 'nov07',
    'Dec-07': 'dec07',
  };

  return (
    fieldMap[columnName] || columnName.toLowerCase().replace(/[^a-z0-9]/g, '')
  );
}

// Helper function to get column width
function getColumnWidth(columnName) {
  const widthMap = {
    No: 60,
    'Index ID': 90,
    Fund: 200, // Increased width for better mobile readability
    'Allocation (%)': 120,
    Inception: 100,
    Strategy: 200, // Increased width for better mobile readability
    'Performance (%)': 130,
    'Standard Deviation (%)': 150,
    'Sharpe Ratio': 120,
    'Oct-07': 80,
    'Nov-07': 80,
    'Dec-07': 80,
  };

  return widthMap[columnName] || 120;
}

// Helper function to determine if column should flex
function shouldFlex(columnName) {
  // No columns should flex to ensure fixed widths work properly on mobile
  return false;
}

// Helper function to get column type
function getColumnType(columnName) {
  const numericColumns = [
    'Index ID',
    'Performance (%)',
    'Standard Deviation (%)',
    'Sharpe Ratio',
    'Allocation (%)',
    'Oct-07',
    'Nov-07',
    'Dec-07',
  ];

  return numericColumns.includes(columnName) ? 'number' : 'string';
}

// Helper function to get row value for a specific column
function getRowValue(row, columnName) {
  const fieldName = getFieldName(columnName);

  // Handle monthly performance data
  if (['Oct-07', 'Nov-07', 'Dec-07'].includes(columnName)) {
    return row.monthlyPerformance?.[columnName] || 0;
  }

  return row[fieldName] !== undefined ? row[fieldName] : '';
}
